/** Opens Compose from anywhere (a person's history, a visit's actions) — the office roles only. */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Role } from '@shared/roles';
import { Sheet } from '@/components/ui/sheet';
import { ComposeSheet, type ComposeOptions } from './ComposeSheet';

export const COMPOSE_ROLES: readonly Role[] = ['admin', 'coadmin', 'scheduler'];

interface ComposeCtx {
  canCompose: boolean;
  openCompose: (opts: ComposeOptions, opener?: HTMLElement | null) => void;
}

const Ctx = createContext<ComposeCtx>({ canCompose: false, openCompose: () => undefined });

export function ComposeProvider({ role, children }: { role: Role | null; children: ReactNode }) {
  const canCompose = !!role && COMPOSE_ROLES.includes(role);
  const [open, setOpen] = useState<{ opts: ComposeOptions; n: number } | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const seq = useRef(0);
  const openCompose = useCallback(
    (opts: ComposeOptions, el?: HTMLElement | null) => {
      if (!canCompose) return;
      opener.current = el ?? (document.activeElement as HTMLElement | null);
      setOpen({ opts, n: ++seq.current });
    },
    [canCompose],
  );
  const value = useMemo(() => ({ canCompose, openCompose }), [canCompose, openCompose]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <Sheet open={!!open} onOpenChange={(o) => (!o ? setOpen(null) : undefined)}>
        {open ? <ComposeSheet key={open.n} opts={open.opts} returnFocusTo={opener} onDone={() => setOpen(null)} /> : null}
      </Sheet>
    </Ctx.Provider>
  );
}

export function useCompose(): ComposeCtx {
  return useContext(Ctx);
}
